import React from "react";
import PropTypes from "prop-types";
import { Checkbox, Table as MuiTable } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import Collapse from "@material-ui/core/Collapse";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import { v4 } from "uuid";
import { translate as t } from "../../../infrastructure/translate/localization";
import CustomPagination from "./CustomPagination";
import EnhancedTableHead from "./EnhancedTableHead";
import useStyles from "./styles";
import CobeeButton from "../CobeeButton";

export default function Table({
  columns,
  data,
  pageNumber,
  pageSize,
  onChange,
  onChangeRowsPerPage,
  totalRows,
  textEllipsisLimit,
  testId,
  withCheckbox,
  setSelectedItems,
  defaultSelected,
  forcePagination,
  withBreakDown,
}) {
  const [showBreakdown, setShowBreakdown] = React.useState("");

  const { root, cell, checkedRow } = useStyles({
    length: data.length,
  });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selected, setSelected] = React.useState([]);

  const handleChangePage = (event, newPage) => {
    if (onChange) {
      onChange(newPage + 1);
      if (forcePagination) {
        setPage(newPage);
      }
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (onChangeRowsPerPage) {
      onChangeRowsPerPage(event.target.value);
    } else {
      setRowsPerPage(event.target.value);
      setPage(0);
    }
  };

  const shouldSlice = (values) => {
    if (
      totalRows > 0 &&
      pageSize > 0 &&
      pageNumber >= 0 &&
      onChange &&
      onChangeRowsPerPage
    ) {
      return values;
    }
    return values.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const textEllipsis = (text) => {
    if (
      textEllipsisLimit &&
      typeof text === "string" &&
      text.length > textEllipsisLimit - 3
    ) {
      return `${text.slice(0, textEllipsisLimit)}...`;
    }
    return text;
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.map(({ id }) => id);
      setSelected(newSelected);
      setSelectedItems(newSelected);
      return;
    }
    setSelected([]);
    setSelectedItems([]);
  };

  const handleCheckbox = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    setSelectedItems(newSelected);
  };

  const isSelected = (name) => {
    if (withCheckbox) {
      return (
        selected.indexOf(name) !== -1 || defaultSelected.indexOf(name) !== -1
      );
    }
    return false;
  };

  return (
    <div className={root}>
      <input data-testid={testId} type="hidden" readOnly value={data.length} />

      <TableContainer>
        <MuiTable stickyHeader aria-label="sticky table">
          <EnhancedTableHead
            withCheckbox={withCheckbox}
            columns={columns}
            numSelected={defaultSelected.length || selected.length}
            onSelectAllClick={handleSelectAllClick}
            rowCount={data.length}
            withBreakDown={withBreakDown}
          />
          <TableBody>
            {shouldSlice(data).map((row, index) => {
              const isItemSelected = isSelected(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <>
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={v4()}
                    className={`${row.checkbox?.props.checked && checkedRow}`}
                    selected={row.checkbox?.props.checked || isItemSelected}
                    onClick={(event) => handleCheckbox(event, row.id)}
                  >
                    {withCheckbox && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>
                    )}
                    {columns.map(({ id, align, format }) => {
                      const value = row[id];
                      return (
                        <TableCell key={v4()} align={align} className={cell}>
                          {textEllipsis(
                            format && typeof value === "number"
                              ? format(value)
                              : value
                          )}
                        </TableCell>
                      );
                    })}

                    {withBreakDown && (
                      <TableCell>
                        <CobeeButton
                          styleType="uncontained"
                          onClick={() => {
                            if (showBreakdown === `breakDown-${index}`) {
                              setShowBreakdown("");
                            } else {
                              setShowBreakdown(`breakDown-${index}`);
                            }
                          }}
                        >
                          {showBreakdown === `breakDown-${index}` ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                          {t("actions.breakdown")}
                        </CobeeButton>
                      </TableCell>
                    )}
                  </TableRow>

                  {withBreakDown && (
                    <TableRow>
                      <TableCell
                        colSpan={columns?.length + 1}
                        style={{ padding: 0, border: "none" }}
                      >
                        <Collapse
                          in={showBreakdown === `breakDown-${index}`}
                          timeout="auto"
                          unmountOnExit
                        >
                          {row?.breakDownContent}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalRows || data.length - 1}
        rowsPerPage={pageSize !== undefined ? pageSize : rowsPerPage}
        page={pageNumber !== undefined ? pageNumber : page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={CustomPagination}
        labelRowsPerPage={`${t("common-messages.rows-per-page")}:`}
        labelDisplayedRows={() => {}}
      />
    </div>
  );
}

Table.defaultProps = {
  data: [],
  pageNumber: undefined,
  pageSize: undefined,
  onChange: undefined,
  onChangeRowsPerPage: undefined,
  totalRows: undefined,
  textEllipsisLimit: undefined,
  testId: "",
  withCheckbox: false,
  setSelectedItems: () => {},
  defaultSelected: [],
  forcePagination: false,
  withBreakDown: false,
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array.isRequired,
  pageNumber: PropTypes.number,
  pageSize: PropTypes.number,
  onChange: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  totalRows: PropTypes.number,
  textEllipsisLimit: PropTypes.number,
  testId: PropTypes.string,
  withCheckbox: PropTypes.bool,
  setSelectedItems: PropTypes.func,
  defaultSelected: PropTypes.array,
  forcePagination: PropTypes.bool,
  withBreakDown: PropTypes.bool,
};
